import {
    init,
    captureMessage,
    captureException,
    thirdPartyErrorFilterIntegration,
} from '@sentry/browser';
import { version } from '../../../../package.json';
import {WIDGET_ASSET_URL} from "../../../Constants/environment";
import {CanceledRequestError} from "../../BookingkitApi/Errors/CanceledRequestError";
import {ClientNetworkError} from "../../BookingkitApi/Errors/ClientNetworkError";
import {
    FloatingWidgetConfigurationError
} from "../../../Composables/useWidgetConfiguration/Errors/FloatingWidgetConfigurationError";

export class SentryLogger implements ILogger {

    private loggingLevel: 'debug' | 'info' | 'warn' | 'error' = "error";

    constructor() {
        init({
            dsn: import.meta.env.VITE_SENTRY_DSN,
            release: version,
            environment: import.meta.env.MODE,
            allowUrls: [`${WIDGET_ASSET_URL}`],
            integrations: function (integrations) {
                // integrations will be all default integrations
                const defaultIntegrations = integrations.filter(function (integration) {
                    return integration.name?.toLowerCase() !== "globalhandlers";
                });
                return [
                    ...defaultIntegrations,
                    thirdPartyErrorFilterIntegration({
                        filterKeys: ['bookingkit-widget-3-0'],
                        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
                    }),
                ];
            },
            ignoreErrors: [
                /Widget with id: `[\w-]+` not found/,
                /java object is gone/i,
                /Importing a module script failed/i,
                /Unable to preload CSS for/i,
                /Failed to fetch dynamically imported module/i,
                /error loading dynamically imported module/i,
                /An event processor returned `null`, will not send event/i,
                /null is not an object \(evaluating '[^']*\.insertBefore'\)/i,
                /Cannot read propert(y|ies) of null \(reading '?insertBefore'?\)/i,
                /can't access property "insertBefore", [\w$.]+ is null/i,
            ],
            beforeSend: (event, hint) => {
                const error = hint?.originalException;
                if (error instanceof CanceledRequestError ||
                    error instanceof ClientNetworkError) {
                    return null;
                }
                // Drop failed async component loads (dynamic import / chunk fetch failures).
                // Vue's defineAsyncComponent catches the rejection and re-wraps it as
                // new Error(String(err)), losing the original message. When the rejection
                // value is undefined this produces Error("undefined") from pendingRequest.
                // This is the same class of transient network error already covered by
                // ignoreErrors for "Failed to fetch dynamically imported module".
                if (error instanceof Error && error.message === 'undefined' &&
                    event.exception?.values?.[0]?.stacktrace?.frames?.some(
                        (f) => f.function === 'pendingRequest' &&
                               !!WIDGET_ASSET_URL &&
                               f.filename?.includes(WIDGET_ASSET_URL)
                    )) {
                    return null;
                }
                // Do not report to sentry if the problem is caused by the widget configuration
                if( error instanceof FloatingWidgetConfigurationError ) {
                    return null;
                }
                // Drop errors that have no widget frame in their stack.
                // Sentry instruments global APIs (setTimeout, addEventListener) at window
                // level and can capture host-page errors. Its own sentryWrapped helper is
                // served from our bundle URL so we exclude @sentry/ frames — they cannot
                // anchor an error to the widget. Any other widget bundle frame (including
                // Vue, axios, intlify etc.) is considered sufficient evidence that the error
                // originated in or was triggered by widget code.
                const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
                if (!WIDGET_ASSET_URL) {
                    return event; // Cannot filter without configured asset URL
                }
                const hasWidgetAppFrame = frames.some(
                    (f) => f.filename?.includes(WIDGET_ASSET_URL) &&
                           !f.filename?.includes('node_modules/@sentry/')
                );
                if (frames.length > 0 && !hasWidgetAppFrame) {
                    return null;
                }
                return event;
            },
        });
    }

    setLoggingLevel(level: 'debug' | 'info' | 'warn' | 'error') {
        this.loggingLevel = level;
    }

    logEvent(event: string, message: string | Object): void {
        if (this.loggingLevel === 'debug') {
            console.log(event, message)
            //captureEvent({message, event_id: event});
        }
    }
    error(error: Error, message?: string): string {
        const errorId = captureException(error);
        console.error(`bk-widget-3:${errorId}|`, error);
        if (message) {
            captureMessage(message);
        }
        return errorId;
    }
}
